import z from 'zod'
import {
  firestoreWriteTimestamp,
  nullSafeOptionalBoolean,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalNumber,
  nullSafeOptionalString,
  nullSafeOptionalStringArray,
  nullSafeStringArray,
} from '../shared/schema'
import { RoomStateDemoType } from './types'

export const soloBotVADOverridesSchema = z.object({
  threshold: nullSafeOptionalNumber().nullable(),
  silenceDurationMs: nullSafeOptionalNumber().nullable(),
  prefixPaddingMs: nullSafeOptionalNumber().nullable(),
  semantic: nullSafeOptionalBoolean().nullable(),
  semanticEagerness: nullSafeOptionalString().nullable(),
})
export type SoloBotVADOverrides = z.infer<typeof soloBotVADOverridesSchema>

export const schema = z.object({
  absentUserIds: nullSafeOptionalStringArray([]),
  groupLeaderUserIds: nullSafeStringArray([]),
  hiddenUserIds: nullSafeStringArray([]),
  userIds: nullSafeStringArray([]),
  slideDeckId: z.string(),
  activeExhibitId: nullSafeOptionalString(),
  activeSlide: nullSafeOptionalNumber().nullable(),
  activeSlideChangedAt: nullSafeOptionalFirestoreTimestamp().nullable(),
  // Set when the group reconvenes off the End-of-Session waiting screen. Drives
  // the group-synced waiting -> processing transition. A one-way latch: once
  // set it is never cleared, and the End-of-Session slide is terminal so the
  // room never returns to a pre-reconvene state. It may be written more than
  // once — the manual and auto-reconvene paths can fire on several clients at
  // once, and the security rules allow a repeat write — but only the presence
  // of a value is ever read, never the value itself.
  endOfSessionReconvenedAt: nullSafeOptionalFirestoreTimestamp().nullable(),
  assignmentId: nullSafeOptionalString(),
  pumpSlide: nullSafeOptionalNumber(),
  soloBotVADOverrides: soloBotVADOverridesSchema.optional().nullable(),
  roomStateName: nullSafeOptionalString(),
  isDemo: nullSafeOptionalBoolean(),
  demoType: z.nativeEnum(RoomStateDemoType).optional().nullable(),
  scheduledAt: nullSafeOptionalFirestoreTimestamp().nullable(),
  roomStartedAt: nullSafeOptionalFirestoreTimestamp(),
  // BL-4216 — snapshot of "was this room launched with exactly one user"
  // captured once at the moment roomStartedAt is first written. Locks the
  // Dynamic Intro Video variant choice so it survives mid-meeting joins.
  // Cleared by roomStateReset alongside roomStartedAt.
  launchedAsSolo: nullSafeOptionalBoolean(),
  // When the live room last emptied (everyone left), so its session is over.
  // Written by the server, never by clients. Not cleared by a reset or when
  // students rejoin.
  livekitEmptiedAt: nullSafeOptionalFirestoreTimestamp(),
  tokenRegenerationRequestedAt: nullSafeOptionalFirestoreTimestamp(),
  sectionId: nullSafeOptionalString(),
  updatedAt: nullSafeOptionalFirestoreTimestamp(),
  transcriberVersion: nullSafeOptionalString(),
  answerObjectPositions: z
    .record(
      z.string(), // questionId
      z.record(
        z.string(), // answerObjectId
        z.object({
          container: z.string(),
          x: z.number(),
          y: z.number(),
          text: nullSafeOptionalString(),
        })
      )
    )
    .optional(),
  deletable: nullSafeOptionalBoolean(),
  promptVersionOverride: z
    .record(
      z.string(), // promptType
      nullSafeOptionalString() // promptVersionId
    )
    .optional()
    .nullable(),
  assignmentRoles: z
    .record(
      z.string(), // userId
      z.string() // roleId
    )
    .optional()
    .nullable(),
  slideMetadata: z
    .record(
      z.string(), // slideId
      z.record(
        z.string(), // key (e.g., sharedDocumentUrl)
        z.string() // value
      )
    )
    .optional()
    .nullable(),
})

export const empty = Object.freeze(
  schema.parse({
    absentUserIds: [],
    groupLeaderUserIds: [],
    hiddenUserIds: [],
    slideDeckId: '',
    userIds: [],
  })
)

export const writeSchema = schema.extend({
  activeSlide: z.number().nullable(),
  activeExhibitId: nullSafeOptionalString().nullable(),
  activeSlideChangedAt: firestoreWriteTimestamp.nullable(),
  endOfSessionReconvenedAt: firestoreWriteTimestamp.nullable(),
  roomStartedAt: firestoreWriteTimestamp,
  scheduledAt: firestoreWriteTimestamp,
  updatedAt: firestoreWriteTimestamp,
})

type FirestoreRoomStateInferred = z.infer<typeof schema>
export type FirestoreRoomState = FirestoreRoomStateInferred
export type FirestoreRoomStateWrite = z.infer<typeof writeSchema>
